import React, { useState, useMemo } from 'react';
import { Calculator, FileText, ChevronDown } from 'lucide-react';
import jsPDF from 'jspdf';
import { motion, AnimatePresence } from 'framer-motion';

// --- Reusable Form Components ---
const FormInput = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
    <input
      {...props}
      className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    />
  </div>
);

const FormSelect = ({ label, children, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
        <div className="relative">
            <select {...props} className="appearance-none w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
                {children}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        </div>
    </div>
);

const TogglePill = ({ options, active, onChange }) => (
  <div className="flex items-center bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
    {options.map(opt => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`px-4 py-1.5 text-sm rounded-full font-semibold transition-colors ${
          active === opt.value ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="bg-slate-100 dark:bg-zinc-800 rounded-xl">
            <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center p-4 font-semibold text-gray-700 dark:text-gray-300">
                {title}
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }}><ChevronDown size={20} /></motion.div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="p-4 pt-0 space-y-4">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function InsulinCalculatorSuite({ patient }) {
  const [weight, setWeight] = useState(patient?.weight || '70');
  const [tdd, setTdd] = useState('35');
  const [bsf, setBsf] = useState('180');
  const [targetBsf, setTargetBsf] = useState('120');
  const [split, setSplit] = useState('50/50');
  const [fromInsulin, setFromInsulin] = useState('NPH');
  const [toInsulin, setToInsulin] = useState('Lantus');
  const [fromDose, setFromDose] = useState('40');
  const [sensitivity, setSensitivity] = useState('Medium');

  const [includedSections, setIncludedSections] = useState({
      weight: true, tdd: true, split: true, icr: true, isf: true, 
      correction: true, conversion: true, slidingScale: true
  });
  const handleIncludeChange = (key) => {
    setIncludedSections(prev => ({...prev, [key]: !prev[key]}));
  };

  const basalRatio = split === '50/50' ? 0.5 : 0.4;
  const bolusRatio = 1 - basalRatio;

  const basal = (parseFloat(tdd) * basalRatio).toFixed(1);
  const bolus = (parseFloat(tdd) * bolusRatio).toFixed(1);
  const icr = (500 / parseFloat(tdd)).toFixed(0);
  const isf = (1800 / parseFloat(tdd)).toFixed(0);
  const correction = ((parseFloat(bsf) - parseFloat(targetBsf)) / isf);

  const insulinMap = useMemo(() => ({
    NPH: { toLantus: 0.8 }, '70/30': { toBasal: 0.7, toBolus: 0.3 },
    Toujeo: { toLantus: 0.8 }, Levemir: { toLantus: 1 },
    Lantus: { toTresiba: 1, toNPH: 1.25 }, Tresiba: { toLantus: 1 }
  }), []);

  const convertedDose = useMemo(() => {
    if (fromInsulin === '70/30') {
      const basal = (parseFloat(fromDose) * insulinMap['70/30'].toBasal).toFixed(0);
      const bolus = ((parseFloat(fromDose) * insulinMap['70/30'].toBolus) / 3).toFixed(0);
      return `${basal} units basal + ${bolus} units bolus TID`;
    }
    const conversionKey = `to${toInsulin}`;
    const conversion = insulinMap[fromInsulin]?.[conversionKey];
    if (conversion) return `${(parseFloat(fromDose) * conversion).toFixed(0)} units ${toInsulin}`;
    if (fromInsulin === toInsulin) return `${parseFloat(fromDose).toFixed(0)} units ${toInsulin} (1:1)`;
    return 'N/A';
  }, [fromInsulin, toInsulin, fromDose, insulinMap]);

  const slidingScale = useMemo(() => {
    const scale = { Low: 1, Medium: 2, High: 3 };
    const baseUnit = scale[sensitivity] || 2;
    return [
        { range: "151-200 mg/dL", units: `${baseUnit} units` },
        { range: "201-250 mg/dL", units: `${baseUnit * 2} units` },
        { range: "251-300 mg/dL", units: `${baseUnit * 3} units` },
        { range: "301-350 mg/dL", units: `${baseUnit * 4} units` },
        { range: "351-400 mg/dL", units: `${baseUnit * 5} units` },
        { range: ">400 mg/dL", units: `${baseUnit * 6} units & Call Provider` },
    ];
  }, [sensitivity]);

  const summary = useMemo(() => {
    let text = "Insulin Regimen Summary:\n";
    if(includedSections.weight) text += `\n• Patient Weight: ${weight} kg`;
    if(includedSections.tdd) text += `\n• Recommended Total Daily Dose (TDD): ${tdd} units`;
    if(includedSections.split) text += `\n• Basal/Bolus Split (${split}): ${basal} units / ${bolus} units`;
    if(includedSections.icr) text += `\n• Insulin-to-Carb Ratio (ICR): 1 unit per ${icr}g carbs`;
    if(includedSections.isf) text += `\n• Insulin Sensitivity Factor (ISF): 1 unit lowers BG ~${isf} mg/dL`;
    if(includedSections.correction) text += `\n• Correction Dose (for ${bsf} mg/dL): ${correction > 0 ? correction.toFixed(0) : 0} units`;
    if(includedSections.conversion) text += `\n• Conversion: ${fromDose} units ${fromInsulin} → ${convertedDose}`;
    if(includedSections.slidingScale) {
        text += `\n\n--- ${sensitivity} Sliding Scale ---\n`;
        slidingScale.forEach(item => { text += `• ${item.range}: ${item.units}\n`; });
    }
    return text;
  }, [weight, tdd, split, basal, bolus, icr, isf, bsf, correction, fromDose, fromInsulin, convertedDose, includedSections, sensitivity, slidingScale]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Insulin Calculation Summary", 20, 30);
    doc.setFontSize(11);
    const lines = doc.splitTextToSize(summary, 170);
    doc.text(lines, 20, 50);
    doc.save("Insulin_Summary.pdf");
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-4">
        <Calculator size={20}/> Insulin Suite
      </h3>
      <div className="space-y-4">
        <CollapsibleSection title="Weight-Based Dosing" defaultOpen>
            <FormInput label="Patient Weight (kg)" type="number" value={weight} onChange={e => setWeight(e.target.value)} />
            <p className="text-sm">T1DM TDD: ~{(weight * 0.4).toFixed(1)} units, T2DM Basal Start: ~{(weight * 0.2).toFixed(1)} units</p>
        </CollapsibleSection>
        <CollapsibleSection title="Basal/Bolus & Ratios">
            <FormInput label="Total Daily Dose (TDD)" type="number" value={tdd} onChange={e => setTdd(e.target.value)} />
            <TogglePill options={[{ label: '50/50', value: '50/50' }, { label: '60/40 (Bolus/Basal)', value: '60/40' }]} active={split} onChange={setSplit} />
            <p className="text-sm">Basal: {basal} units, Bolus: {bolus} units</p>
            <p className="text-sm">ICR: 1 unit per {icr}g carbs</p>
            <p className="text-sm">ISF: 1 unit lowers ~{isf} mg/dL</p>
        </CollapsibleSection>
        <CollapsibleSection title="Correction Dose">
            <div className="grid grid-cols-2 gap-4">
                <FormInput label="Current BSF (mg/dL)" type="number" value={bsf} onChange={e => setBsf(e.target.value)} />
                <FormInput label="Target BSF (mg/dL)" type="number" value={targetBsf} onChange={e => setTargetBsf(e.target.value)} />
            </div>
            <p className="text-sm font-semibold text-center">Correction Dose: {correction > 0 ? correction.toFixed(0) : 0} units</p>
        </CollapsibleSection>
        <CollapsibleSection title="Insulin Conversion">
            <FormInput label="From Dose (units)" type="number" value={fromDose} onChange={e => setFromDose(e.target.value)} />
            <div className="grid grid-cols-2 gap-4">
                <FormSelect label="From Insulin" value={fromInsulin} onChange={e => setFromInsulin(e.target.value)}>
                    {Object.keys(insulinMap).map(name => <option key={name} value={name}>{name}</option>)}
                </FormSelect>
                <FormSelect label="To Insulin" value={toInsulin} onChange={e => setToInsulin(e.target.value)}>
                    {Object.keys(insulinMap).map(name => <option key={name} value={name}>{name}</option>)}
                </FormSelect>
            </div>
            <p className="text-sm font-semibold text-center">Converted Dose: {convertedDose}</p>
        </CollapsibleSection>
        <CollapsibleSection title="Sliding Scale Generator">
            <p className="text-sm text-gray-600 dark:text-gray-400">Generate a standard sliding scale based on insulin sensitivity.</p>
            <TogglePill 
                options={[{label: 'Low', value: 'Low'}, {label: 'Medium', value: 'Medium'}, {label: 'High', value: 'High'}]}
                active={sensitivity}
                onChange={setSensitivity}
            />
            <div className="bg-white dark:bg-zinc-700 rounded-lg p-4 mt-2">
                <h4 className="font-semibold mb-2 text-gray-700 dark:text-gray-300">{sensitivity} Sensitivity Scale:</h4>
                <ul className="space-y-1 text-sm">
                    {slidingScale.map((item, i) => (
                        <li key={i} className="flex justify-between">
                            <span>{item.range}</span>
                            <span className="font-medium">{item.units}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </CollapsibleSection>
      </div>
      <div className="mt-8 bg-blue-50 dark:bg-zinc-900 border border-blue-100 dark:border-zinc-700 rounded-xl p-5">
        <h4 className="text-sm font-bold text-blue-600 dark:text-blue-300 uppercase mb-3">Summary & Export</h4>
        <div className="mb-4">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Include in Summary:</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.keys(includedSections).map(key => (
                    <label key={key} className="flex items-center text-sm space-x-2 cursor-pointer">
                        <input type="checkbox" checked={includedSections[key]} onChange={() => handleIncludeChange(key)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                        <span className="capitalize">{key === 'slidingScale' ? 'Sliding Scale' : key}</span>
                    </label>
                ))}
            </div>
        </div>
        <pre className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap bg-white dark:bg-zinc-800 p-3 rounded-md">{summary}</pre>
        <div className="flex gap-4 mt-4">
          <button onClick={() => navigator.clipboard.writeText(summary)} className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 font-semibold text-sm">Copy Summary</button>
          <button onClick={exportToPDF} className="px-4 py-2 bg-slate-200 text-gray-800 rounded-full hover:bg-slate-300 font-semibold text-sm flex items-center gap-2"><FileText size={16} /> Export PDF</button>
        </div>
      </div>
    </div>
  );
}
